import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Article } from "@/data/articles";
import {
  FALLBACK_IMAGE,
  DEFAULT_CATEGORY_SLUG,
  DEFAULT_READING_TIME,
  formatArticleDate,
  titleCaseSlug,
} from "@/lib/article";

export type SearchSort = "newest" | "oldest" | "title";

export interface SearchParams {
  q: string;
  cat: string;
  sort: SearchSort;
  page: number;
  pageSize?: number;
  /** When > 0, only include articles published in the last N days. */
  publishedWithinDays?: number;
}

export type SearchResultItem = Article;

export function useSearchResults({
  q,
  cat,
  sort,
  page,
  pageSize = 9,
  publishedWithinDays = 0,
}: SearchParams) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["search", { q, cat, sort, page, pageSize, publishedWithinDays }],
    enabled: q.trim().length >= 1,
    queryFn: async () => {
      const { data, error: rpcError } = await supabase.rpc("search_articles", {
        q,
        cat: cat || "all",
        sort_by: sort,
        page_num: page,
        page_size: pageSize,
        published_within_days: publishedWithinDays,
      });

      if (rpcError) throw rpcError;

      const rows = data || [];

      const results: SearchResultItem[] = rows.map((row) => {
        const catSlug = row.category_slug || DEFAULT_CATEGORY_SLUG;
        const catName = row.category_name || titleCaseSlug(catSlug);
        return {
          id: row.id,
          title: row.title,
          slug: row.slug,
          excerpt: row.excerpt || "",
          image: row.featured_image || FALLBACK_IMAGE,
          category: catSlug,
          categoryLabel: catName,
          categoryColor: row.category_color ?? null,
          categoryImage: row.category_image ?? null,
          date: formatArticleDate(row.published_at || row.created_at),
          publishedAt: row.published_at || row.created_at,
          readingTime: row.reading_time || DEFAULT_READING_TIME,
          tags: row.tags || [],
        };
      });

      return {
        results,
        total: rows[0]?.total_count ? Number(rows[0].total_count) : 0,
      };
    },
    placeholderData: (previousData) => previousData,
  });

  return {
    results: data?.results || [],
    total: data?.total || 0,
    loading: isLoading,
    error: error as Error | null,
    refresh: refetch,
  };
}
